import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from './useMultiTenant';
import { captureException } from '@/lib/monitoring';

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type SupplierTripStatus =
  | 'draft'
  | 'vazios_em_transito'
  | 'aguardando_coleta'
  | 'cheios_em_transito'
  | 'concluida'
  | 'rateio_pendente'
  | 'rateio_confirmado';

export interface SupplierTripItem {
  id: string;
  supplier_trip_id: string;
  company_id: string;
  product_id: string;
  quantity_empty_sent: number;
  quantity_empty_confirmed: number | null;
  quantity_full_received: number | null;
  purchase_id: string | null;
  freight_rateio_value: number | null;
  rateio_confirmed: boolean;
  created_at: string;
  product?: { name: string; sku: string | null };
}

export interface SupplierTrip {
  id: string;
  company_id: string;
  supplier_id: string;
  vehicle_id: string;
  driver_user_id: string;
  trip_number: string;
  status: SupplierTripStatus;
  departure_date: string;
  departure_km: number | null;
  arrival_supplier_km: number | null;
  arrival_supplier_at: string | null;
  supplier_signature_data: string | null;
  supplier_signed_at: string | null;
  supplier_signed_by: string | null;
  supplier_signature_ip: string | null;
  departure_supplier_at: string | null;
  departure_supplier_km: number | null;
  arrival_company_km: number | null;
  arrival_company_at: string | null;
  total_km: number | null;
  total_fuel_cost: number | null;
  estimated_trip_cost: number | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Joins
  supplier?: { name: string } | null;
  vehicle?: { license_plate: string; model: string; fuel_consumption: number | null } | null;
  driver?: { full_name: string } | null;
  items?: SupplierTripItem[];
}

export interface CreateSupplierTripPayload {
  company_id: string;
  supplier_id: string;
  vehicle_id: string;
  driver_user_id: string;
  departure_date: string;
  departure_km: number;
  notes?: string | null;
  items: Array<{ product_id: string; quantity_empty_sent: number }>;
}

export interface ConfirmArrivalPayload {
  trip_id: string;
  arrival_supplier_km: number;
  items_confirmed: Array<{ item_id: string; quantity_empty_confirmed: number }>;
  supplier_signed_by: string;
  supplier_signature_data: string;
  supplier_signature_ip?: string | null;
  will_return_today?: boolean;
}

export interface StartReturnPayload {
  trip_id: string;
  departure_supplier_km: number;
  items_received: Array<{
    item_id: string;
    quantity_full_received: number;
    purchase_id?: string | null;
  }>;
}

export interface CloseTripPayload {
  trip_id: string;
  arrival_company_km: number;
}

export interface ConfirmRateioPayload {
  trip_id: string;
  items: Array<{
    item_id: string;
    freight_rateio_value: number;
    purchase_id?: string | null;
  }>;
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

export interface SupplierTripsFilters {
  status?: SupplierTripStatus | null;
  driver_user_id?: string | null;
}

// ─── Hook principal ───────────────────────────────────────────────────────────

export const useSupplierTrips = (
  filters?: SupplierTripsFilters,
  options?: { enabled?: boolean },
) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();

  // ── Query: listagem ──────────────────────────────────────────────────────
  const {
    data: trips,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['supplier-trips', companyId, filters?.status, filters?.driver_user_id],
    queryFn: async () => {
      let query = supabase
        .from('supplier_trips')
        .select(`
          *,
          supplier:suppliers!supplier_id(name),
          vehicle:vehicles!vehicle_id(license_plate, model, fuel_consumption),
          driver:profiles!driver_user_id(full_name)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.driver_user_id) {
        query = query.eq('driver_user_id', filters.driver_user_id);
      }

      const { data, error: qErr } = await query;
      if (qErr) throw qErr;
      return (data || []) as SupplierTrip[];
    },
    enabled: !!companyId && (options?.enabled ?? true),
  });

  // ── Query: contagem de rateio_pendente para badge ─────────────────────────
  const { data: pendingRateioCount } = useQuery({
    queryKey: ['supplier-trips-pending-rateio', companyId],
    queryFn: async () => {
      const { count, error: cErr } = await supabase
        .from('supplier_trips')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'rateio_pendente');
      if (cErr) throw cErr;
      return count ?? 0;
    },
    enabled: !!companyId && (options?.enabled ?? true),
    staleTime: 0,
  });

  // ── Query: detalhe de viagem com itens ───────────────────────────────────
  const useTripById = (id: string | null) =>
    useQuery({
      queryKey: ['supplier-trip', id],
      queryFn: async () => {
        const { data, error: qErr } = await supabase
          .from('supplier_trips')
          .select(`
            *,
            supplier:suppliers!supplier_id(name),
            vehicle:vehicles!vehicle_id(license_plate, model, fuel_consumption),
            driver:profiles!driver_user_id(full_name),
            items:supplier_trip_items(
              *,
              product:products!product_id(name, sku)
            )
          `)
          .eq('id', id!)
          .single();
        if (qErr) throw qErr;
        return data as SupplierTrip;
      },
      enabled: !!id && !!companyId,
      staleTime: 0,
    });

  const invalidate = (tripId?: string) => {
    queryClient.invalidateQueries({ queryKey: ['supplier-trips', companyId] });
    queryClient.invalidateQueries({ queryKey: ['supplier-trips-pending-rateio', companyId] });
    if (tripId) {
      queryClient.invalidateQueries({ queryKey: ['supplier-trip', tripId] });
    }
  };

  // ── Mutation: criar viagem ────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (payload: CreateSupplierTripPayload) => {
      const { data, error: rpcErr } = await supabase.rpc('create_supplier_trip', {
        p_company_id:      payload.company_id,
        p_supplier_id:     payload.supplier_id,
        p_vehicle_id:      payload.vehicle_id,
        p_driver_user_id:  payload.driver_user_id,
        p_departure_date:  payload.departure_date,
        p_departure_km:    payload.departure_km,
        p_notes:           payload.notes ?? null,
        p_items:           payload.items,
      });
      if (rpcErr) throw rpcErr;
      return data as { id: string; trip_number: string };
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Viagem ao fornecedor criada com sucesso!' });
    },
    onError: (err: Error) => {
      captureException(err, { context: 'supplier_trips', action: 'create' });
      toast({
        title: 'Erro ao criar viagem',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  // ── Mutation: confirmar chegada ───────────────────────────────────────────
  const confirmArrivalMutation = useMutation({
    mutationFn: async (payload: ConfirmArrivalPayload) => {
      const { data, error: rpcErr } = await supabase.rpc('confirm_supplier_arrival', {
        p_trip_id:                  payload.trip_id,
        p_arrival_supplier_km:      payload.arrival_supplier_km,
        p_items_confirmed:          payload.items_confirmed,
        p_supplier_signed_by:       payload.supplier_signed_by,
        p_supplier_signature_data:  payload.supplier_signature_data,
        p_supplier_signature_ip:    payload.supplier_signature_ip ?? null,
        p_will_return_today:        payload.will_return_today ?? false,
      });
      if (rpcErr) throw rpcErr;
      return data as { status: string };
    },
    onSuccess: (_, vars) => {
      invalidate(vars.trip_id);
      toast({ title: 'Chegada no fornecedor confirmada!' });
    },
    onError: (err: Error) => {
      captureException(err, { context: 'supplier_trips', action: 'confirm_arrival' });
      toast({ title: 'Erro ao confirmar chegada', description: err.message, variant: 'destructive' });
    },
  });

  // ── Mutation: iniciar retorno ─────────────────────────────────────────────
  const startReturnMutation = useMutation({
    mutationFn: async (payload: StartReturnPayload) => {
      const { data, error: rpcErr } = await supabase.rpc('start_return_trip', {
        p_trip_id:               payload.trip_id,
        p_departure_supplier_km: payload.departure_supplier_km,
        p_items_received:        payload.items_received,
      });
      if (rpcErr) throw rpcErr;
      return data as { status: string };
    },
    onSuccess: (_, vars) => {
      invalidate(vars.trip_id);
      toast({ title: 'Retorno iniciado com sucesso!' });
    },
    onError: (err: Error) => {
      captureException(err, { context: 'supplier_trips', action: 'start_return' });
      toast({ title: 'Erro ao iniciar retorno', description: err.message, variant: 'destructive' });
    },
  });

  // ── Mutation: fechar viagem ───────────────────────────────────────────────
  const closeTripMutation = useMutation({
    mutationFn: async (payload: CloseTripPayload) => {
      const { data, error: rpcErr } = await supabase.rpc('close_supplier_trip', {
        p_trip_id:            payload.trip_id,
        p_arrival_company_km: payload.arrival_company_km,
      });
      if (rpcErr) throw rpcErr;
      return data;
    },
    onSuccess: (_, vars) => {
      invalidate(vars.trip_id);
      toast({ title: 'Viagem concluída! Rateio de frete pendente.' });
    },
    onError: (err: Error) => {
      captureException(err, { context: 'supplier_trips', action: 'close_trip' });
      toast({ title: 'Erro ao fechar viagem', description: err.message, variant: 'destructive' });
    },
  });

  // ── Query: viagens disponíveis (sem motorista) para assumir ─────────────
  const useAvailableTrips = () =>
    useQuery({
      queryKey: ['supplier-trips-available', companyId],
      queryFn: async () => {
        const { data, error: qErr } = await supabase
          .from('supplier_trips')
          .select(`
            *,
            supplier:suppliers!supplier_id(name),
            vehicle:vehicles!vehicle_id(license_plate, model)
          `)
          .is('driver_user_id', null)
          .in('status', ['draft', 'vazios_em_transito'])
          .order('departure_date', { ascending: true });
        if (qErr) throw qErr;
        return (data || []) as SupplierTrip[];
      },
      enabled: !!companyId && (options?.enabled ?? true),
      staleTime: 30000,
    });

  // ── Mutation: assumir viagem ──────────────────────────────────────────────
  const assumeTripMutation = useMutation({
    mutationFn: async (tripId: string) => {
      const { data, error: rpcErr } = await supabase.rpc('assume_supplier_trip', {
        p_trip_id: tripId,
      });
      if (rpcErr) throw rpcErr;
      return data as { trip_id: string; trip_number: string };
    },
    onSuccess: (_, tripId) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-trips-available', companyId] });
      queryClient.invalidateQueries({ queryKey: ['supplier-trips', companyId] });
      queryClient.invalidateQueries({ queryKey: ['supplier-trip', tripId] });
      toast({ title: 'Viagem assumida com sucesso!' });
    },
    onError: (err: Error & { code?: string; details?: string }) => {
      captureException(err, { context: 'supplier_trips', action: 'assume' });
      if (err.code === '23505') {
        toast({
          title: 'Viagem já assumida',
          description: 'Esta viagem já foi assumida por outro motorista.',
          variant: 'destructive',
        });
      } else if (err.code === '23514') {
        toast({
          title: 'Operação inválida',
          description: err.details ?? err.message,
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Erro ao assumir viagem', description: err.message, variant: 'destructive' });
      }
    },
  });

  // ── Mutation: confirmar rateio ────────────────────────────────────────────
  const confirmRateioMutation = useMutation({
    mutationFn: async (payload: ConfirmRateioPayload) => {
      const { data, error: rpcErr } = await supabase.rpc('confirm_freight_rateio', {
        p_trip_id: payload.trip_id,
        p_items:   payload.items,
      });
      if (rpcErr) throw rpcErr;
      return data;
    },
    onSuccess: (_, vars) => {
      invalidate(vars.trip_id);
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      toast({ title: 'Rateio de frete confirmado com sucesso!' });
    },
    onError: (err: Error) => {
      captureException(err, { context: 'supplier_trips', action: 'confirm_rateio' });
      toast({ title: 'Erro ao confirmar rateio', description: err.message, variant: 'destructive' });
    },
  });

  return {
    trips: trips || [],
    isLoading,
    error,
    refetch,
    pendingRateioCount: pendingRateioCount ?? 0,
    useTripById,
    useAvailableTrips,
    createTrip: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    confirmArrival: confirmArrivalMutation.mutateAsync,
    isConfirmingArrival: confirmArrivalMutation.isPending,
    startReturn: startReturnMutation.mutateAsync,
    isStartingReturn: startReturnMutation.isPending,
    closeTrip: closeTripMutation.mutateAsync,
    isClosingTrip: closeTripMutation.isPending,
    confirmRateio: confirmRateioMutation.mutateAsync,
    isConfirmingRateio: confirmRateioMutation.isPending,
    assumeTrip: assumeTripMutation.mutateAsync,
    isAssumingTrip: assumeTripMutation.isPending,
  };
};
